import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { useHousehold } from '../context/HouseholdContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TECHNIQUES = [
  ['cover_drive', 'Cover drive'],
  ['straight_drive', 'Straight drive'],
  ['pull', 'Pull'],
  ['sweep', 'Sweep'],
  ['defence_front', 'Front-foot defence'],
  ['yorker', 'Yorker'],
  ['inswing', 'Inswing'],
  ['outswing', 'Outswing'],
  ['leg_spin', 'Leg spin'],
  ['off_spin', 'Off spin'],
  ['bouncer', 'Bouncer'],
  ['diving_catch', 'Diving catch'],
];

/**
 * Pro Clip Library — browse + watch + AI breakdown.
 *
 * Route: /clips
 * Query params:
 *   ?focus=<clip_id>     auto-open the clip detail panel
 *   ?tab=saved           start on the saved tab
 */
export default function ProClipLibrary() {
  const { token } = useAuth();
  const { activeAthlete } = useHousehold();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [tab, setTab] = useState(params.get('tab') || 'browse');
  const [tag, setTag] = useState(params.get('tag') || '');
  const [clips, setClips] = useState([]);
  const [savedClips, setSavedClips] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clips`, {
        params: { technique_tag: tag || undefined, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setClips(r.data?.clips || []);
    } catch (err) {
      console.error('Failed to load clips:', err);
      toast.error('Could not load clips');
    } finally {
      setLoading(false);
    }
  }, [tag, token]);

  const loadSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clips/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedClips(r.data?.clips || []);
    } catch (err) { console.warn('Failed to load saved clips:', err); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  // Auto-open a focused clip from query params.
  useEffect(() => {
    const focusId = params.get('focus');
    if (!focusId || active?.id === focusId) return;
    axios.get(`${API}/clips/${focusId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.data?.clip && setActive(r.data.clip)).catch(() => {});
  }, [params, token, active]);

  const onTagChange = (next) => {
    setTag(next);
    const newParams = new URLSearchParams(params);
    if (next) newParams.set('tag', next); else newParams.delete('tag');
    setParams(newParams, { replace: true });
  };

  const open = async (clip) => {
    setActive(clip);
    if (activeAthlete) {
      axios.post(`${API}/clips/${clip.id}/impression`, {
        athlete_id: activeAthlete.id, served_via: 'browse',
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    if (!clip.ai_breakdown) {
      try {
        const r = await axios.get(`${API}/clips/${clip.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.data?.clip) setActive(r.data.clip);
      } catch (err) { console.warn('Failed to fetch clip detail:', err); }
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-6xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="opacity-70 hover:opacity-100">← Back</button>
        <Logo size={28} />
        <div className="flex-1" />
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Pro clip library</h1>
          <p className="text-sm opacity-70 mt-1">
            Short clips of great cricketers, with a 30-second AI breakdown
            you can use to copy the technique.
          </p>
        </div>

        <Tabs value={tab} onChange={setTab} options={[
          ['browse', 'Browse'],
          ['saved', `Saved (${savedClips.length})`],
        ]} />

        {tab === 'browse' && (
          <>
            <Filters tag={tag} onTagChange={onTagChange} />
            <ClipGrid
              clips={clips}
              loading={loading}
              onOpen={open}
              theme={theme}
            />
          </>
        )}

        {tab === 'saved' && (
          <ClipGrid
            clips={savedClips}
            loading={false}
            empty="Save clips you want to come back to."
            onOpen={open}
            theme={theme}
          />
        )}
      </main>

      {active && (
        <ClipDetail
          clip={active}
          token={token}
          onClose={() => {
            setActive(null);
            const p = new URLSearchParams(params);
            p.delete('focus');
            setParams(p, { replace: true });
          }}
          onSavedChange={loadSaved}
          theme={theme}
        />
      )}
    </div>
  );
}

// ---------- pieces ----------

function Tabs({ value, onChange, options }) {
  return (
    <div className="flex gap-2">
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className="px-3.5 py-1.5 rounded-full text-sm font-semibold"
          style={{
            background: value === v
              ? 'linear-gradient(90deg, rgba(0,166,81,0.18), rgba(0,217,255,0.18))'
              : 'rgba(255,255,255,0.04)',
            border: `1px solid ${value === v ? 'rgba(0,201,167,0.4)' : 'rgba(150,160,180,0.25)'}`,
            color: value === v ? '#00C9A7' : 'inherit',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Filters({ tag, onTagChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Pill active={!tag} onClick={() => onTagChange('')}>All</Pill>
      {TECHNIQUES.map(([v, label]) => (
        <Pill key={v} active={tag === v} onClick={() => onTagChange(v)}>{label}</Pill>
      ))}
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="text-xs px-3 py-1.5 rounded-full"
      style={{
        background: active ? 'rgba(0,201,167,0.18)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${active ? 'rgba(0,201,167,0.4)' : 'rgba(150,160,180,0.2)'}`,
        color: active ? '#00C9A7' : 'inherit',
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function ClipGrid({ clips, loading, empty, onOpen, theme }) {
  if (loading) return <p className="opacity-60 text-sm">Loading clips…</p>;
  if (!clips.length) {
    return (
      <div
        className="rounded-2xl p-8 text-center text-sm opacity-70"
        style={{
          background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(0,201,167,0.15)',
        }}
      >
        {empty || 'No clips here yet — try a different filter.'}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {clips.map((c) => <ClipTile key={c.id} clip={c} onClick={() => onOpen(c)} theme={theme} />)}
    </div>
  );
}

function ClipTile({ clip, onClick, theme }) {
  const player = clip.player?.name;
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl overflow-hidden transition-transform hover:-translate-y-0.5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
        border: '1px solid rgba(150,160,180,0.2)',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000' }}>
        {clip.thumbnail_url && (
          <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
        )}
        <span
          className="absolute bottom-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}
        >
          {clip.duration_seconds || 30}s
        </span>
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm leading-tight line-clamp-2">{clip.title}</div>
        {player && (
          <div className="text-xs opacity-60 mt-1 truncate">{player}</div>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {(clip.technique_tags || []).slice(0, 2).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(0,201,167,0.12)', color: '#00C9A7' }}>
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}

function ClipDetail({ clip, token, onClose, onSavedChange, theme }) {
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();

  const toggleSave = async () => {
    try {
      if (saved) {
        await axios.delete(`${API}/clips/${clip.id}/save`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSaved(false);
      } else {
        await axios.post(`${API}/clips/${clip.id}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSaved(true);
      }
      onSavedChange();
    } catch { toast.error('Could not save'); }
  };

  const breakdown = clip.ai_breakdown;
  const drillId = breakdown?.suggested_drill_id;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl overflow-hidden"
        style={{
          background: '#0a0e27', color: '#f7fafc',
          border: '1px solid rgba(0,201,167,0.3)',
          maxWidth: 720, width: '100%',
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        }}
      >
        <div className="flex items-center justify-between px-4 py-3"
             style={{ borderBottom: '1px solid rgba(150,160,180,0.15)' }}>
          <div className="min-w-0 pr-4">
            <div className="font-bold truncate">{clip.title}</div>
            {clip.player?.name && (
              <div className="text-xs opacity-60 truncate">
                {clip.player.name}{clip.player.country ? ` · ${clip.player.country}` : ''}
              </div>
            )}
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100 text-xl">✕</button>
        </div>

        <div style={{ position: 'relative', aspectRatio: '16/9', background: '#000' }}>
          {clip.embed_html ? (
            <div
              className="w-full h-full"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(clip.embed_html, {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'referrerpolicy'],
              }) }}
            />
          ) : (
            <a
              href={clip.source_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center w-full h-full text-sm"
              style={{ color: '#00C9A7' }}
            >
              Open original on {clip.source}
            </a>
          )}
        </div>

        <div className="p-4 overflow-y-auto space-y-4 text-sm">
          {breakdown ? (
            <>
              <Section title="What's happening">
                {breakdown.technique}
              </Section>
              <Section title="Why it works">
                {breakdown.why_it_works}
              </Section>
              <Section title="Try this today" accent>
                {breakdown.try_this}
              </Section>
              {breakdown.skill_highlights && Object.keys(breakdown.skill_highlights).length > 0 && (
                <SkillHighlights data={breakdown.skill_highlights} />
              )}
            </>
          ) : (
            <p className="opacity-60">AI breakdown is being generated…</p>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={toggleSave}
              className="px-3 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: saved ? 'rgba(0,201,167,0.2)' : 'rgba(255,255,255,0.06)',
                color: saved ? '#00C9A7' : '#f7fafc',
                border: `1px solid ${saved ? 'rgba(0,201,167,0.4)' : 'rgba(150,160,180,0.25)'}`,
              }}
            >
              {saved ? '✓ Saved' : '☆ Save'}
            </button>
            {drillId && (
              <button
                onClick={() => navigate('/drills')}
                className="px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)', color: '#fff' }}
              >
                Practice this technique →
              </button>
            )}
            <a
              href={clip.source_url} target="_blank" rel="noopener noreferrer"
              className="px-3 py-2 rounded-lg text-sm opacity-60 hover:opacity-100"
            >
              Open on {clip.source} ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-widest font-bold mb-1"
        style={{ color: accent ? '#00C9A7' : '#94a3b8' }}
      >
        {title}
      </div>
      <div className="opacity-90 leading-relaxed">{children}</div>
    </div>
  );
}

function SkillHighlights({ data }) {
  const entries = Object.entries(data);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#94a3b8' }}>
        Skill highlights
      </div>
      <div className="space-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <div className="w-32 opacity-80">{k.replace(/_/g, ' ')}</div>
            <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(0, Math.min(100, (v / 10) * 100))}%`,
                  background: 'linear-gradient(90deg, #00A651, #00D9FF)',
                }}
              />
            </div>
            <div className="w-8 text-right tabular-nums">{v.toFixed(1)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
